/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useMemo } from "react";
import "./App.css";
import {
  Card,
  Flex,
  Typography,
  Carousel,
  Space,
  Button,
  Divider,
  Avatar,
  Modal,
} from "antd";
import {
  QuestionCircleFilled,
  EnvironmentOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { getIcon } from "./helper";
import API from "./AppUtils";
import { getObjectSize } from "./global";
import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import env from "./env.json";
import AvailabilityDatesCalender from "./components/AvailabilityDatesCalender";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { calculatePriceForParty } from "./common/helper";
import {
  addUsersTourDataToUrl,
  getUsersTourDataFromUrl,
} from "./common/helper";
import "react-calendar/dist/Calendar.css";
import { DateTime } from "luxon";
import { MdStars } from "react-icons/md";
import { GLOBAL_ADVANCE_NOTICE_DAYS_BEFORE_BOOKING } from "./common/constants";
import tz_lookup from "tz-lookup";
import AgendaItemsRevised from "./components/AgendaItemsRevised";
import CategoryPill from "./components/CategoryPill";
import LoadingSkeletonTourDetails from "./components/LoadingSkeletonTourDetails";
import Navbar from "./components/Navbar";
import GuideServicePill from "./components/GuideServicePill";
import DownloadWegetherModal from "./components/DownloadWegetherModal";
import Popover from "./components/Popover";
import ParticipantsControlModal from "./components/ParticipantsControlModal";
import PartyIcon from "./components/PartyIcon";
import Markdown from "react-markdown";
import MarkdownText from "./components/MarkdownText";
import NotFound from "./NotFound";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import GuideBadges from "./components/GuideBadges";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const calculatedDefaultStartDate = dayjs().add(7, "day").format("YYYY-MM-DD");
const calculatedDefaultEndDate = dayjs().add(14, "day").format("YYYY-MM-DD");

const GOOGLE_API_KEY = env.GOOGLE_API_KEY;
const { Title, Text } = Typography;
const PARAMS_DATA = [
  {
    id: 1,
    data: "Tokyo",
    name: "Location",
    icon: () => getIcon("Location"),
  },
  {
    id: 2,
    data: "Food Tour",
    name: "Tour Type",
    icon: () => getIcon("MdLocalDining"),
  },
  {
    id: 3,
    data: "Walking",
    name: "Transportation",
    icon: () => getIcon("MdDirectionsWalk"),
  },
  {
    id: 4,
    data: "Persons",
    name: "Participants",
    icon: () => getIcon("MdGroups"),
  },
  {
    id: 5,
    data: "Time",
    name: "Duration",
    icon: () => getIcon("MdTimer"),
  },
];

const COLOR = [
  "#0399b4",
  "#bf49c6",
  "#b43726",
  "#4c96e2",
  "#a702c5",
  "#6d131b",
  "#5790e3",
  "#3f2003",
  "#9320c4",
  "#c2388f",
  "#f811ff",
  "#2e12a6",
  "#36adb0",
  "#20a316",
];

const restrictionsStyle = {
  padding: "2px 10px",
  border: "1px solid",
  borderRadius: 20,
};

const TourDetails = () => {
  const { expId } = useParams();
  const navigate = useNavigate();
  const initialUsersTourDataJson = useMemo(() => {
    try {
      return getUsersTourDataFromUrl(window.location.href);
    } catch (error) {
      window.location.href = "/not-found";
    }
  }, []);
  const defaultTourData = {
    experienceId: expId,
    partyType: "Shared",
    partySize: 1,
    experienceName: "",
    selectedTime: null,
    searchedLocationLabel: "",
    guests: { adults: 1, children: 0, elderly: 0 },
    startDate: calculatedDefaultStartDate,
    endDate: calculatedDefaultEndDate,
    latLongDestination: { lat: 0, lng: 0 },
  };

  let initialUsersTourData = initialUsersTourDataJson || defaultTourData;

  const [tourData, setTourData] = useState(null);
  const [isLess, setIsLess] = useState(false);
  const [position, setPosition] = useState({ lat: 53.54992, lng: 10.00678 });
  const [paramsData, setParamsData] = useState(PARAMS_DATA);
  const [tourType, setTourType] = useState(initialUsersTourData?.tourType);
  const [agendaData, setAgendaData] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [displayedDateTime, setDisplayedDateTime] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState({});
  const [titleLevel, setTitleLevel] = useState({});
  const [downloadAppModalVisible, setDownloadAppModalVisible] = useState(false);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [partyType, setPartyType] = useState(initialUsersTourData?.partyType);
  const [guests, setGuests] = useState(initialUsersTourData?.guests || {});
  const [guestModalVisible, setGuestModalVisible] = useState(false);
  // Define breadcrumbs with truncated experience name
  const breadcrumbs = [
    {
      label: "Home",
      path: addUsersTourDataToUrl("/", {
        ...initialUsersTourData,
        guests: guests,
        partyType: partyType,
        partySize: guests?.adults + guests?.children + guests?.elderly,
      }),
    },
    { label: tourData?.experienceName, path: `/experience/${expId}` },
  ];
  function formatCurrency(value) {
    // Ensure the input is a number
    if (typeof value !== "number") {
      throw new Error("Input must be a number");
    }

    // Format the number to two decimal points and add dollar sign
    return `$${value.toFixed(2)}`;
  }

  const handleTabChange = (key) => {
    if (key === "shared") {
      setPartyType("Shared");
    } else {
      setPartyType("Private");
    }
  };

  const handleGuestChange = (type, operation) => {
    setGuests((prevState) => ({
      ...prevState,
      [type]: Math.max(
        prevState[type] + (operation === "increment" ? 1 : -1),
        0
      ),
    }));
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 500) {
        setTitleLevel({ main: 2, mid: 3, sub: 4 });
      } else {
        setTitleLevel({ main: 3, mid: 4, sub: 5 });
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchTourDetails = async (id) => {
    let response;
    let supp;
    if (id) {
      response = await API.getTourDetails(id);
      setTourData(response);
      setPosition({
        lat: response?.location?.lat,
        lng: response?.location?.long,
      });
      setTourType(response?.tourType);
      let params = paramsData;
      let arr = [
        response?.location?.formatedAdd,
        response?.tourType.join(", "),
        response?.transportationMethod.join(", "),
        response?.maxParticipants,
        response?.duration,
      ];
      for (let i = 0; i < params.length; i++) {
        params[i].data = arr[i];
      }
      setParamsData(params);
      console.log("result after setting --> ", response);
      if (response.supplierId) {
        supp = await API.getSupplierById(await response.supplierId);
        setSupplier(supp);
        console.log("1", supp, "2", supplier);
      }
      setPartyType((prev) => {
        if (
          response?.experienceType?.toLowerCase().includes(prev?.toLowerCase())
        ) {
          return prev;
        } else if (response?.experienceType?.toLowerCase().includes("shared")) {
          return "Shared";
        } else if (
          response?.experienceType?.toLowerCase().includes("private")
        ) {
          return "Private";
        }
      });

      initialUsersTourData = {
        ...initialUsersTourData,
        experienceName: response?.experienceName,
        latLongDestination: {
          lat: response?.location?.lat,
          lng: response?.location?.long,
        },
      };
    } else {
      console.log(" there is no id to fetch ");
    }
    const data = response;
    let agenda_data = [];
    let currObj;
    let startItem = {
      id: 1,
      title:
        data?.agendaStartLocation.formatedAdd &&
        data?.agendaStartLocation.formatedAdd !== null
          ? data?.agendaStartLocation.formatedAdd
          : "N/A",
      description:
        data?.agendaStartDescription && data?.agendaStartDescription !== null
          ? data?.agendaStartDescription
          : " ",
      icon: (tintColor) => <EnvironmentOutlined />,
    };

    agenda_data.push(startItem);
    if (
      data.agendaItems &&
      data.agendaItems !== null &&
      data.agendaItems !== undefined &&
      getObjectSize(data.agendaItems) !== 0
    ) {
      data.agendaItems.map((item, key) => {
        let keys = Object.keys(item);
        if (getObjectSize(keys) !== 0) {
          currObj = {
            id: key + 2,
            title: item.title && item.title !== null ? item.title : "N/A",
            description:
              item.description && item.description !== null
                ? item.description
                : " ",
            images:
              item.pictures &&
              item.pictures !== null &&
              item.pictures !== undefined &&
              getObjectSize(item.pictures) !== 0
                ? item.pictures
                : [],
            icon: (tintColor) => <EnvironmentOutlined />,
          };
          agenda_data.push(currObj);
        }
      });
    }
    let endItem = {
      id: getObjectSize(agenda_data) + 1,
      title:
        data.agendaEndLocation.formatedAdd &&
        data.agendaEndLocation.formatedAdd !== null
          ? data.agendaEndLocation.formatedAdd
          : "N/A",
      description:
        data.agendaEndDescription && data.agendaEndDescription !== null
          ? data.agendaEndDescription
          : " ",
      icon: (tintColor) => <EnvironmentOutlined />,
    };
    agenda_data.push(endItem);
    setAgendaData(agenda_data);
  };

  useEffect(() => {
    fetchTourDetails(expId)
      .then((data) => {
        //if (!data) navigate('/notfound');
      })
      .catch(() => navigate("/notfound"));
  }, [expId, navigate]);

  useEffect(() => {
    if (tourData?.location) {
      const lat = parseFloat(tourData?.location.lat);
      const lng = parseFloat(tourData?.location.lng);

      if (!isNaN(lat) && !isNaN(lng)) {
        setPosition({ lat, lng });
      }
    }
  }, [tourData]);

  const randomSeed = Math.random();

  const handleDateTimeSelect = (dateTime) => {
    setSelectedDateTime(dateTime);
    setDisplayedDateTime(
      `${dateTime.date} ${dateTime.startTime} - ${dateTime.endTime}`
    );
  };

  const handleSupportMail = () => {
    const subject = encodeURIComponent(
      `Support Request Regarding Experience ID: ${expId}`
    );
    const body = encodeURIComponent(
      `I need booking assistance with the following experience:

- Experience Name: ${tourData?.experienceName}
- Experience ID: ${expId}
- Experience Link: https://wegether.click/experience/${expId}
- Experience Date: SPECIFY DATE AND TIME DESIRED FOR BOOKING`
    );

    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=contact@wegethertravel.com&su=${subject}&body=${body}`,
      "_blank"
    );
  };

  const handleCheckout = () => {
    const calendarElement = document.getElementById("calendar");

    if (!displayedDateTime) {
      // Smooth scroll to the calendar div
      window.scrollTo({
        top: calendarElement.offsetTop,
        behavior: "smooth",
      });

      // Add the highlight-border class to the calendar element
      calendarElement.classList.add("blinking-border");

      // Remove the class after 3 seconds
      setTimeout(() => {
        calendarElement.classList.remove("blinking-border");
      }, 1500);

      console.log("please select date and time");
    } else {
      const timezoneOfTripCreator = () => {
        const lat = tourData?.location?.lat;
        const long = tourData?.location?.long;

        return tz_lookup(lat, long);
      };

      const usersTourSummary = {
        experienceId: tourData?.id,
        startTime: tourData?.startTime?.[0]?.startTime ?? "Default Start Time",
        endTime: tourData?.startTime?.[0]?.endTime ?? "Default End Time",
        partyType: partyType,
        partySize: guests?.adults + guests?.children + guests?.elderly,
        experienceName: tourData?.experienceName,
        selectedTime: selectedDateTime,
        searchedLocationLabel: initialUsersTourData?.searchedLocationLabel,
        guests: guests,
        startDate: initialUsersTourData?.startDate,
        endDate: initialUsersTourData?.endDate,
        latLongDestination: initialUsersTourData?.latLongDestination,
        timeZone: timezoneOfTripCreator(),
      };

      let res = addUsersTourDataToUrl(
        `/experience/${usersTourSummary?.experienceId}/checkout`,
        usersTourSummary
      );

      navigate(res);
      window.scrollTo(0, 0);
    }
  };

  function getPossessiveForm(name) {
    if (!name) return name;

    name = name.trim();

    if (name.toLowerCase().endsWith("s")) {
      return name + "'";
    } else {
      return name + "'s";
    }
  }

  if (tourData) {
    return (
      <div
        style={{
          backgroundColor: "#f0f2f5",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Navbar
          headerCheckoutButtonText={
            displayedDateTime ? "Checkout" : "Choose Date & Time"
          }
          headerCheckoutButtonTextOnClick={handleCheckout}
          breadcrumbs={breadcrumbs}
          headerDownloadButtonOnClick={() => setDownloadAppModalVisible(true)}
        />
        <div
          style={{
            height: "4.3rem",
          }}
        ></div>
        <Flex
          gap={window.innerWidth < 600 ? "small" : "large"}
          align="center"
          className="main-container-tour-detail-page"
          vertical
        >
          <Flex
            gap={"small"}
            align="center"
            // className="main-container-tour-detail-page"
            vertical
          >
            <Flex align="center" justify="center">
              {tourData?.experienceName ? (
                <Flex
                  align="center"
                  justify="center"
                  style={{ flexWrap: "wrap", gap: "0.35rem" }}
                >
                  <Title
                    className="margin0"
                    level={titleLevel.main}
                    style={{
                      textAlign: "center",
                      paddingTop: window.innerWidth < 770 ? "10px" : "0px",
                      marginBottom: 0,
                    }}
                  >
                    {tourData.experienceName}

                    {/* {tourData?.advanceNoticeDays <= 3 &&
                  window.innerWidth > 600 &&
                      (
                      <Popover
                        title="Featured Experience"
                        content={
                          <div style={{ maxWidth: "450px" }}>
                            Hosted by featured guides and available to book on
                            short notice
                          </div>
                        }
                      >
                        <MdStars
                          size={28}
                          color="#FA5705"
                          style={{
                            marginLeft: "0.25rem",
                            cursor: "pointer",
                            position: "relative",
                            top: "-2px",
                            verticalAlign: "middle",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popover>
                    )} */}
                  </Title>
                </Flex>
              ) : null}
            </Flex>

            {tourData?.advanceNoticeDays <= 3 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // marginBottom: "0.5rem",
                  // padding: "1rem",
                  // paddingTop: "0.5rem",
                  // paddingBottom: "0.5rem",
                  // boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  backgroundColor: "transparent",
                  borderRadius: "10px",
                  // border: "1px solid #FA5705",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    color: "gray",
                    fontSize: "0.8rem",
                  }}
                >
                  {tourData?.advanceNoticeDays <= 3 && (
                    <Popover
                      title="Featured Experience"
                      content={
                        <div style={{ maxWidth: "450px" }}>
                          Hosted by featured guides and available to book on
                          short notice
                        </div>
                      }
                    >
                      <MdStars
                        size={28}
                        color="#FA5705"
                        style={{
                          marginLeft: "0.25rem",
                          cursor: "pointer",
                          position: "relative",
                          top: "-2px",
                          verticalAlign: "middle",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popover>
                  )}{" "}
                  Featured Experience
                </div>
              </div>
            )}

            <Flex
              gap={"small"}
              // className="margin-top-bottom"
              style={{ flexWrap: "wrap", justifyContent: "center" }}
              size={"large"}
            >
              {tourData &&
                tourType.map((item, index) => {
                  return (
                    <CategoryPill category={item} key={index} index={index} />
                  );
                })}
            </Flex>
          </Flex>

          {tourData ? (
            <div
              style={{ height: "60vh", width: "100%" }}
              className="margin-top-bottom"
            >
              <Carousel autoplay arrows speed={2000} effect="fade">
                {tourData?.coverPhotos?.map((photo, index) => (
                  <div
                    key={`exp_photo_reel_${index}`}
                    style={{ clipPath: "inset(0 0 0 0)" }}
                  >
                    <img
                      key={`${photo.image}_blur`}
                      src={photo.image}
                      alt=""
                      style={{
                        filter: "blur(75px) brightness(95%)",
                        clipPath: "inset(0 0 0 0)",
                        height: "60vh",
                        width: "calc(100% + 100px)",
                        marginLeft: "-50px",
                      }}
                    />
                    <img
                      key={photo.image}
                      src={photo.image}
                      alt=""
                      style={{
                        maxHeight: "60vh",
                        objectFit: "cover",
                        margin: "auto",
                        position: "relative",
                        top: "-60vh",
                        marginBottom: "-60vh",
                      }}
                    />
                  </div>
                ))}
              </Carousel>
            </div>
          ) : null}
          <Flex
            style={{
              width: "100%",
              flexWrap: "wrap",
              flexGrow: 1,
              flexDirection: "row-reverse",
            }}
            className="margin-top-bottom"
            justify="space-around"
          >
            <Flex
              vertical
              style={{
                backgroundColor: "white",
                display: "flex",

                maxWidth: "500px",
                width: "100%",
                borderRadius: 5,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                height: window.innerWidth < 1200 ? "auto" : "284.81px",
                // padding: "30px"
              }}
            >
              <Flex
                style={{
                  padding: "24px",
                  height: "100%",
                  paddingTop: "32px",
                  paddingBottom: "32px",
                  minHeight: "150px",
                }}
                vertical
                justify="space-between"
              >
                <Flex
                  style={{
                    paddingTop: window.innerWidth < 420 ? "0px" : "5px",
                    flexDirection: window.innerWidth < 420 ? "column" : "row",
                    alignItems: window.innerWidth < 420 ? "center" : "center",
                    gap: window.innerWidth < 420 ? "10px" : "0px",
                    marginBottom: window.innerWidth < 420 ? "10px" : "0px",
                    justifyContent: "space-between",
                  }}
                >
                  <Flex
                    onClick={() => setIsContactModalVisible(true)} // Directly opening modal
                    style={{ cursor: "pointer", paddingRight: "1rem" }}
                  >
                    <div style={{ width: "50px", height: "50px" }}>
                      <Avatar size={50} src={supplier?.data?.profilePicUrl} />
                    </div>
                    <Title
                      level={5}
                      style={{
                        marginLeft: "10px",
                        marginTop: "auto",
                        marginBottom: "auto",
                        fontWeight: "700",
                      }}
                    >
                      {supplier?.data?.name}
                    </Title>
                    <InfoCircleOutlined
                      style={{ color: "#FA5705", marginLeft: 8 }}
                    />
                  </Flex>
                  <Flex
                    style={{ width: window.innerWidth < 420 ? "100%" : "auto" }}
                  >
                    <Button
                      style={{
                        backgroundColor: "#FA5705",
                        color: "white",
                        whiteSpace: "nowrap",
                        textWrap: "ellipsis",
                        width: window.innerWidth < 420 ? "100%" : "auto",
                        fontWeight: window.innerWidth < 420 ? "600" : "600",
                        fontSize: window.innerWidth < 420 ? "20px" : "16px",
                        borderRadius: window.innerWidth < 420 ? "30px" : "5px",
                        height: "2.5rem",
                        fontFamily: "poppins",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      }}
                      type="primary"
                      icon={
                        <MessageOutlined
                          style={{
                            color: "white",
                            fontWeight: window.innerWidth < 420 ? "600" : "400",
                            fontSize: window.innerWidth < 420 ? "20px" : "16px",
                          }}
                        />
                      }
                      size={"large"}
                      onClick={() => setDownloadAppModalVisible(true)} // Show modal on button click
                    >
                      {}
                      Contact
                    </Button>
                  </Flex>
                </Flex>
                {/* <GuideBadges /> */}
                <button
                  onClick={handleCheckout}
                  // type="primary"
                  // size="medium
                  style={{
                    color: "white",
                    paddingLeft: "30px !important",
                    paddingRight: "30px !important",
                    maxWidth: "500px !important",
                    width: "100%",
                    backgroundColor: "#FA5705",
                    borderRadius: 30,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "3px",
                    fontFamily: "poppins",

                    // height: "px",
                    // marginTop: "67px", // Space between buttons
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    border: "none",
                    height: "2.5rem",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      fontSize: "20px",
                      fontWeight: "600",
                    }}
                    level={titleLevel.sub}
                  >
                    {displayedDateTime ? "Checkout" : "Choose Date & Time"}
                  </span>
                </button>
              </Flex>
            </Flex>

            <Card
              style={{
                width: "100%",
                maxWidth: "500px",
                marginTop: window.innerWidth < 1200 ? "10px" : "0px",
                borderRadius: 5,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
              }}
            >
              <div style={{ margin: "0px !important" }}>
                <div
                  style={{
                    fontWeight: "500",
                    color: "#606060",
                    fontSize: "1.5rem",
                    marginBottom: "15px",
                    textAlign: "center",
                    width: "100% !important",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {formatCurrency(
                    calculatePriceForParty(
                      tourData?.experienceType || "Shared",
                      tourData?.baseSharedPrice,
                      tourData?.basePrivatePrice,
                      tourData?.extraPersonPrice,
                      partyType,
                      guests?.adults + guests?.children + guests?.elderly
                    )
                  )}{" "}
                  <span
                    style={{
                      fontSize: "1.2rem",
                      color: "#606060",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingLeft: "7px",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        verticalAlign: "baseline",
                        gap: "2px",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          lineHeight: "1",
                          transform: "translateY(-2px)",
                        }}
                      >
                        {"("}
                      </span>{" "}
                      <PartyIcon partyType={partyType} />{" "}
                      {guests?.adults + guests?.children + guests?.elderly}{" "}
                      {partyType}{" "}
                      <span
                        style={{
                          display: "inline-block",
                          lineHeight: "1",
                          transform: "translateY(-2px)",
                        }}
                      >
                        {")"}
                      </span>
                    </span>
                    <EditOutlined
                      onClick={() => setGuestModalVisible(true)}
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        color: "rgb(250, 87, 5)",
                      }}
                    />
                  </span>
                </div>
              </div>
              <Space
                size={1}
                direction="vertical"
                style={{ width: "100%", height: "100%" }}
              >
                <Flex
                  justify="space-between"
                  style={{
                    width: "100%",
                    height: "100%",
                    flexDirection: window.innerWidth < 500 ? "column" : "row",
                  }}
                >
                  {tourData &&
                    tourData?.experienceType?.includes("Private") && (
                      <Space
                        direction="vertical"
                        style={{
                          width: "100%",
                          justifyItems: "center",
                          alignItems: "center",
                          padding: "0px",
                        }}
                        justify="center"
                        align="center"
                      >
                        {/* Private section title */}
                        <div
                          className="margin0"
                          style={{
                            fontSize: "1.2rem",
                            fontWeight: "500",
                            color: "black",
                          }}
                        >
                          {"Private Experience"}
                        </div>

                        {/* Table-like layout for prices using Antd's Space with flex */}
                        <Space
                          direction="vertical"
                          style={{
                            width: "100%",
                            marginTop: window.innerWidth < 500 ? "0.5rem" : "0",
                            marginBottom:
                              window.innerWidth < 500 ? "1.5rem" : "0",
                          }}
                        >
                          <Space
                            style={{
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Text
                              style={{
                                color: "grey",
                                wordBreak: "keep-all",
                                fontWeight: "500",
                                fontSize: "1.2rem",
                              }}
                              className="margin0"
                            >
                              Base Price:
                            </Text>
                            <Text
                              className="margin0"
                              style={{
                                color: "#606060",
                                wordBreak: "keep-all",
                                fontWeight: "bold",
                                fontSize: "1.2rem",
                              }}
                            >
                              {"$" +
                                Number(tourData.basePrivatePrice).toFixed(2)}
                            </Text>
                          </Space>

                          <Space
                            style={{
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                            align="center"
                          >
                            <Text
                              style={{
                                color: "grey",
                                wordBreak: "keep-all",
                                fontWeight: "500",
                                fontSize: "1.2rem",
                              }}
                              className="margin0"
                            >
                              Extra Person:
                            </Text>

                            <Text
                              className="margin0"
                              style={{
                                color: "#606060",
                                wordBreak: "keep-all",
                                fontWeight: "bold",
                                fontSize: "1.2rem",
                              }}
                            >
                              {"$" +
                                Number(tourData.extraPersonPrice).toFixed(2)}
                            </Text>
                          </Space>
                        </Space>
                      </Space>
                    )}

                  <div
                    style={{
                      height: "100%",
                      width: "10px",
                    }}
                  ></div>

                  {tourData && tourData?.experienceType?.includes("Shared") && (
                    <Space
                      direction="vertical"
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        className="margin0"
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "500",
                          color: "black",
                        }}
                      >
                        {"Shared Experience"}
                      </div>

                      {/* Table-like layout for prices using Antd's Space with flex */}
                      <Space
                        direction="vertical"
                        style={{
                          width: "100%",
                          marginTop: window.innerWidth < 500 ? "0.5rem" : "0",
                          marginBottom:
                            window.innerWidth < 500 ? "0.5rem" : "0",
                        }}
                      >
                        <Space
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                          }}
                          align="center"
                        >
                          <Text
                            style={{
                              color: "grey",
                              wordBreak: "keep-all",
                              fontWeight: "500",
                              fontSize: "1.2rem",
                            }}
                            className="margin0"
                          >
                            Base Price:
                          </Text>
                          <Text
                            className="margin0"
                            style={{
                              color: "#606060",
                              fontWeight: "bold",

                              fontSize: "1.2rem",
                            }}
                            level={titleLevel.sub}
                          >
                            {"$" + Number(tourData.baseSharedPrice).toFixed(2)}
                          </Text>
                        </Space>

                        <Space
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                          }}
                          align="center"
                        >
                          <Text
                            className="margin0"
                            style={{
                              color: "grey",
                              wordBreak: "keep-all",
                              fontWeight: "500",
                              fontSize: "1.2rem",
                            }}
                            level={titleLevel.sub}
                          >
                            Extra Person:
                          </Text>

                          <Text
                            className="margin0"
                            style={{
                              color: "#606060",
                              wordBreak: "keep-all",
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                            }}
                            level={titleLevel.sub}
                          >
                            {"$" + Number(tourData.extraPersonPrice).toFixed(2)}
                          </Text>
                        </Space>
                      </Space>
                    </Space>
                  )}
                </Flex>

                {/* Cancellation Policy */}
                {tourData && tourData?.cancellationPolicy && (
                  <div style={{ width: "100%" }}>
                    <Title
                      level={titleLevel.sub}
                      style={{
                        border: "1px solid black",
                        padding: "3px 8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "100%", // Makes Title take full width
                        borderRadius: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {/* Full-width clickable element with centered content */}
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          paddingTop: "2px",
                          paddingBottom: "2px",
                        }}
                      >
                        {tourData.cancellationPolicy + " Cancellation "}
                        <Popover
                          content={
                            <>
                              <Text
                                style={{ fontWeight: "500", color: "#252525" }}
                              >
                                <table>
                                  <tbody>
                                    <tr>
                                      <td>Strict Cancellation:</td>
                                      <td>Full refund</td>
                                      <td>14</td>
                                      <td>days before experience date.</td>
                                    </tr>
                                    <tr>
                                      <td>
                                        Moderate
                                        Cancellation:&nbsp;&nbsp;&nbsp;&nbsp;
                                      </td>
                                      <td>Full refund</td>
                                      <td>&nbsp;7</td>
                                      <td>days before experience date.</td>
                                    </tr>
                                    <tr>
                                      <td>Flexible Cancellation:</td>
                                      <td>Full refund</td>
                                      <td>&nbsp;3</td>
                                      <td>days before experience date.</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </Text>
                              <br />
                              <Text
                                style={{ fontWeight: "500", color: "#606060" }}
                              >
                                Please refer to our&nbsp;
                                <a
                                  target="_blank"
                                  href="https://wegethertravel.com/cancellation-policy"
                                  style={{ color: "#fa5705" }}
                                >
                                  Cancellation Policy FAQ
                                </a>{" "}
                                for more information.
                              </Text>
                            </>
                          }
                          title={<h3>Cancellation Policy</h3>}
                        >
                          {/* Popover icon */}
                          <QuestionCircleFilled
                            style={{ color: "#FA5705", marginLeft: 8 }}
                          />
                        </Popover>
                      </div>
                    </Title>
                  </div>
                )}
              </Space>
            </Card>
          </Flex>

          {/* <Card
            style={{
              maxWidth: "1400px",
              width: "100%",
              height: "auto",
              margin: "auto",
              borderRadius: 5,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            bodyStyle={{
              padding: "2rem",
              height: "100%",
              width: "100%",
            }}
          >
            <Flex
              style={{
                width: "100%",
                flexDirection:
                  typeof window !== "undefined" && window.innerWidth < 500
                    ? "column"
                    : "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 
                  typeof window !== "undefined" && window.innerWidth < 500
                    ? "0rem"
                    : "2rem",
              }}
            >
              <img
                src="/certified-badge.png"
                alt="Certified Badge"
                style={{
                  width: "212px",
                  height: "212px",
                  objectFit: "contain",
                  marginTop: "-5px"
                }}
              />
              <img
                src="/authentic-badge.png"
                alt="Authentic Badge"
                style={{
                  width: "200px",
                  height: "200px",
                  objectFit: "contain",
                }}
              />
            </Flex>
            <p
              style={{
                color: "grey",
                fontSize: "1.1rem",
                fontWeight: "500",
                textAlign: "left",
                marginTop: "5px",
                lineHeight: "1.5",
              }}
            >
              Our guides are officially certified by accredited local authorities, including professional societies, government bodies, and leading experts. Explore your destination with a knowledgeable and vetted professional.
            </p>
            <p
              style={{
                color: "grey",
                fontSize: "1.1rem",
                fontWeight: "500",
                textAlign: "left",
                marginTop: "5px",
                lineHeight: "1.5",
              }}
            >
              As part of the very fabric of their communities with lifetimes of inherited knowledge, our guides offer a genuine window into local culture and traditions. Foster your connection to locals with our guide as your personal ambassador.
            </p>
          </Card> */}

          <Flex horizontal wrap justify="center" gap={"small"} size={"large"}>
            {tourData &&
              tourData?.restrictions?.map((item, index) => {
                const randomColor =
                  COLOR[
                    (Math.floor(randomSeed * COLOR.length) + index) %
                      COLOR.length
                  ];
                const restrictionStyle = {
                  ...restrictionsStyle,
                  color: randomColor,
                  borderColor: randomColor,
                  backgroundColor: "#FEFEFE",
                  padding: "3px 8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                };
                return (
                  <Title
                    key={`restriction${index}`}
                    className="margin0"
                    level={titleLevel.sub}
                    style={restrictionStyle}
                  >
                    {item}
                  </Title>
                );
              })}
          </Flex>
          <MarkdownText
            description={tourData?.experienceDescription || ""}
            expandable={true}
            lessMargin={false}
          />

          <div
            style={{
              height: "60vh",
              alignSelf: "center",
              width: "100%",
            }}
            className="margin-top-bottom"
          >
            <APIProvider apiKey={GOOGLE_API_KEY}>
              <Map center={position} defaultZoom={10} mapId="DEMO_MAP_ID">
                <AdvancedMarker position={position} />
              </Map>
            </APIProvider>
          </div>
          <Flex
            gap="middle"
            align="center"
            style={{ width: "100%" }}
            vertical
            className="margin-top-bottom"
          >
            <Flex align="center" vertical>
              <Title className="margin0" level={titleLevel.mid}>
                Inclusions
              </Title>
              <Title
                className="margin0"
                level={titleLevel.sub}
                style={{ color: "gray" }}
              >
                Here are some specifics on what this experience provides.
              </Title>
            </Flex>
            <Flex
              justify="center"
              align="stretch"
              wrap="wrap"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                width: "100%",
                justifyContent: "center",
              }}
            >
              {tourData?.inclusion?.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    // Flex-grow for regular screens
                    maxWidth: 350, // Max width for desktop
                    margin: 4,
                    justifyContent: "stretch",

                    // Add responsive behavior for mobile views
                    width: "100%", // Default width for mobile (full width)
                    "@media (minWidth: 500px)": {
                      flex: "1 1 300px", // Revert to original behavior for screens wider than 500px
                      maxWidth: 350,
                    },
                  }}
                >
                  <Card
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 auto",
                      justifyContent: "start",
                      fontSize: "1.2rem",
                      borderRadius: 5,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    <Card.Meta
                      avatar={getIcon(item.inclusionType)}
                      title={
                        <div
                          style={{
                            fontWeight: "500",
                            fontSize: "1.2rem",
                            lineHeight: "25px",
                          }}
                        >
                          {item.inclusionType}
                        </div>
                      }
                      style={{ fontSize: "1.2rem" }}
                      description={
                        <pre
                          className="margin0"
                          style={{
                            fontSize: "1.2rem",
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            color: "gray",
                            fontFamily: "system-ui",
                            fontWeight: "500",
                          }}
                        >
                          <MarkdownText
                            description={item?.inclusionDescription || ""}
                            expandable={false}
                            lessMargin={true}
                            lessListVerticalSpace={true}
                          />
                        </pre>
                      }
                    />
                  </Card>
                </div>
              ))}
            </Flex>
          </Flex>
          {tourData && tourData?.suggestedToBring && (
            <Flex
              vertical
              align="center"
              className="margin-top-bottom"
              style={{ width: "100%" }}
            >
              <Title className="margin0" level={titleLevel.mid}>
                Suggested to Bring
              </Title>
              <Card
                styles={{ body: { padding: "0", margin: "auto" } }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  // flex: "1 1 auto",
                  justifyContent: "center",
                  marginTop: "2rem",
                  maxWidth: "670px",
                  width: "100%",
                  padding: "0",
                  borderRadius: 5,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                }}
              >
                <Card.Meta
                  style={{
                    padding: "0",
                  }}
                  // avatar={getIcon(item.inclusionType)}
                  // title={"Suggested To Bring"}
                  description={
                    <div
                      style={{
                        fontSize: "1.2rem",
                        padding: "20px 20px 20px 10px",
                        color: "gray",
                        fontFamily: "system-ui",
                        fontWeight: "500",
                      }}
                    >
                      <MarkdownText
                        description={tourData?.suggestedToBring || ""}
                        expandable={false}
                        lessMargin={false}
                      />
                    </div>
                  }
                />
              </Card>
            </Flex>
          )}
          {tourData && tourData?.agendaDescription && (
            <Flex
              justify="space-evenly"
              gap={"large"}
              className="margin-top-bottom"
              vertical
            >
              <Typography style={{ alignSelf: "center" }}>
                <Title
                  className="margin0"
                  style={{ textAlign: "center", width: "100%" }}
                  level={titleLevel.mid}
                >
                  Proposed Agenda
                </Title>
                <Title
                  className="margin0 subheading-answer"
                  style={{ textAlign: "center", width: "100%" }}
                  level={titleLevel.sub}
                >
                  <MarkdownText
                    description={tourData?.agendaDescription || ""}
                    expandable={false}
                    lessMargin={false}
                  />
                </Title>
              </Typography>
              <Button
                onClick={() => setIsLess(!isLess)}
                type="primary"
                style={{
                  color: "white",
                  padding: "20px",
                  backgroundColor: "green",
                  borderRadius: 15,
                  maxWidth: "670px",
                  margin: "auto",
                  width: "100%",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
              >
                <Title
                  className="margin0"
                  style={{ color: "white" }}
                  level={titleLevel.sub}
                >
                  {isLess ? "View Less" : "View Full Agenda"}
                </Title>
              </Button>
              {isLess && <AgendaItemsRevised agendaItems={agendaData} />}
              <Flex
                gap={"middle"}
                style={{
                  margin: "auto",
                  maxWidth: "85rem",
                  width: "fit-content",
                  marginTop: isLess ? undefined : "1.1rem",
                }}
                justify="start"
                vertical
              >
                <Card style={{ paddingInline: "0px" }}>
                  {paramsData.map((item, itemIdx) => (
                    <Flex
                      key={`agenda_item${itemIdx}`}
                      align="center"
                      justify="space-between"
                      gap={"large"}
                    >
                      <Flex
                        gap={"small"}
                        align="center"
                        justify="start"
                        style={{ flex: 0, minWidth: "165px" }}
                      >
                        {item.icon()}
                        <Title className="margin0" level={titleLevel.sub}>
                          {item.name}
                        </Title>
                      </Flex>
                      <Title
                        level={titleLevel.sub}
                        className="margin0"
                        style={{ textAlign: "center", flex: 0 }}
                      >
                        :
                      </Title>
                      <Title
                        level={titleLevel.sub}
                        className="margin0 subheading-answer"
                        style={{ flex: 1, textAlign: "left" }}
                      >
                        {item.data}
                      </Title>
                    </Flex>
                  ))}
                </Card>
              </Flex>
            </Flex>
          )}
          <Flex
            gap={"small"}
            align="center"
            vertical
            className="margin-top-bottom"
            flex={1}
          >
            <Title className="margin0" level={titleLevel.mid}>
              Availability
              <Popover
                content={
                  <pre
                    style={{
                      fontFamily: "system-ui",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontWeight: "500",
                      color: "#f05901",
                      maxWidth: "450px",
                    }}
                  >
                    <span
                      style={{ color: "grey" }}
                    >{`Available times based in the local timezone where the experience is taking place: `}</span>
                    {`${
                      tourData?.location?.lat &&
                      tourData?.location?.long &&
                      tz_lookup(
                        tourData?.location?.lat,
                        tourData?.location?.long
                      )
                    }`}
                    <p style={{ color: "grey" }}>
                      Questions about special accomodations or if your desired
                      booking date is not on the calendar?
                    </p>
                    <p style={{ color: "grey" }}>
                      Please reach out to our support team for additional
                      assistance.
                    </p>
                  </pre>
                }
                title="Timezone Notice"
              >
                <QuestionCircleFilled
                  style={{ color: "#F05901", marginLeft: "0.3rem" }}
                />
              </Popover>
            </Title>
            <Title className="margin0 subheading-answer" level={titleLevel.sub}>
              View{" "}
              {getPossessiveForm(
                supplier?.data?.profileData?.firstName?.trim()
              ) || ""}{" "}
              availability within your travel dates.
            </Title>

            <Flex
              align="center"
              gap={"middle"}
              style={{
                flexDirection: window.innerWidth < 736 ? "column" : "row",
                width: "100%",
                marginTop: 8,
              }}
            >
              <div
                style={{
                  marginTop: 8,
                  flex: 1,
                  maxWidth: "352px",
                  height: "32px",
                }}
              >
                <Title
                  className="margin0"
                  level={titleLevel.sub}
                  style={{
                    border: "1px solid gray",
                    borderRadius: 20,
                    padding: "2px 10px",
                    backgroundColor: "#FEFEFE",
                    width: "352px",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  LAST UPDATED:{" "}
                  {DateTime?.fromISO(tourData?.updatedAt || "")
                    ?.toRelative()
                    ?.toUpperCase()}
                </Title>
              </div>

              <div
                style={{
                  marginTop: 8,
                  flex: 1,
                  width: "100%",
                  maxWidth: "352px",
                  height: "32px",
                }}
              >
                <Title
                  style={{
                    border: "1px solid gray",
                    borderRadius: 20,
                    padding: "2px 10px",
                    backgroundColor: "#FEFEFE",
                    width: "100%",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    width: "352px",
                  }}
                  className="margin0"
                  level={titleLevel.sub}
                >
                  ADVANCE NOTICE:{" "}
                  {tourData?.advanceNoticeDays ||
                    GLOBAL_ADVANCE_NOTICE_DAYS_BEFORE_BOOKING}{" "}
                  DAYS
                  <Popover
                    content={
                      <Text style={{ fontWeight: "500", color: "#f05901" }}>
                        <span
                          style={{ color: "grey" }}
                        >{`Booking restricted `}</span>
                        {`within ${
                          tourData?.advanceNoticeDays ||
                          GLOBAL_ADVANCE_NOTICE_DAYS_BEFORE_BOOKING
                        } days `}
                        <span
                          style={{ color: "grey" }}
                        >{`from experience start`}</span>
                      </Text>
                    }
                    title="Advance Notice Days"
                  >
                    <QuestionCircleFilled
                      style={{ color: "#F05901", marginLeft: "0.3rem" }}
                    />
                  </Popover>
                </Title>
              </div>
            </Flex>

            {tourData?.availableTimes &&
            Object.keys(tourData?.availableTimes).length > 0 ? (
              <Flex
                id="calendar"
                vertical
                align="center"
                justify="space-evenly"
                style={{ marginTop: "1rem", marginBottom: "1rem" }}
                gap={"large"}
              >
                <AvailabilityDatesCalender
                  availableTimes={tourData?.availableTimes || {}}
                  handleDateTimeSelect={handleDateTimeSelect}
                  lat={tourData?.location?.lat}
                  long={tourData?.location?.long}
                  advanceNoticeDays={tourData?.advanceNoticeDays || "7"}
                />
              </Flex>
            ) : null}
          </Flex>
          {displayedDateTime && (
            <Title
              size="large"
              className="margin0"
              style={{
                color: "black",
                border: "1px solid red",
                backgroundColor: "white",
                borderColor: "red",
                padding: "4px",
                maxWidth: "500px",
                width: "100%",
                borderRadius: 15,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Title className="margin0" level={titleLevel.sub}>
                Reserved for: {displayedDateTime}
              </Title>

              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setDisplayedDateTime(null);
                }}
                style={{
                  marginLeft: "10px",
                  cursor: "pointer",
                  color: "black",
                  fontSize: "15px",
                }}
              >
                ✕
              </span>
            </Title>
          )}

          <Button
            onClick={handleCheckout}
            type="primary"
            size="large"
            className="glowing-button"
            style={{
              color: "white",
              padding: "20px",
              paddingLeft: "30px",
              paddingRight: "30px",
              maxWidth: "500px",
              width: "100%",
              backgroundColor: "#FA5705",
              borderRadius: 15,
              border: "none",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Title
              className="margin0"
              style={{ color: "white" }}
              level={titleLevel.sub}
            >
              {displayedDateTime ? "Checkout" : "Choose Date & Time"}
            </Title>
          </Button>

          <Button
            onClick={handleSupportMail}
            type="primary"
            size="large"
            className="glowing-button"
            style={{
              color: "white",
              padding: "20px",
              paddingLeft: "30px",
              paddingRight: "30px",
              maxWidth: "500px",
              width: "100%",
              backgroundColor: "#008000",
              borderRadius: 15,
              border: "none",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Title
              className="margin0"
              style={{ color: "white" }}
              level={titleLevel.sub}
            >
              {"Need Assistance"}
              <Popover
                content={
                  <div style={{ maxWidth: "450px" }}>
                    <Text style={{ fontWeight: "500", color: "grey" }}>
                      <p>
                        Questions about special accomodations or if your desired
                        booking date is not on the calendar?
                      </p>
                      <p>
                        Please reach out to our support team for additional
                        assistance.
                      </p>
                    </Text>
                  </div>
                }
                title="Contact Support"
              >
                <QuestionCircleFilled
                  style={{ marginLeft: "10px" }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popover>
            </Title>
          </Button>
        </Flex>

        <DownloadWegetherModal
          downloadAppModalVisible={downloadAppModalVisible}
          setDownloadAppModalVisible={setDownloadAppModalVisible}
        />

        <Modal
          open={isContactModalVisible}
          onCancel={() => setIsContactModalVisible(false)} // Directly closing modal
          footer={null} // Optional: Remove default footer buttons
          width={"100%"}
          style={{
            maxWidth: "85rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
          }}
        >
          <div
            style={{ textAlign: "center", marginBottom: "1rem", width: "100%" }}
          >
            {/* <Avatar size={100} src={supplier?.data?.profilePicUrl} /> */}
            <img
              src={supplier?.data?.profilePicUrl}
              style={{
                maxWidth: "20rem",
                width: "100%",
                borderRadius: "0.5rem",
              }}
            />
            <Title level={4} style={{ marginTop: "1rem" }}>
              {supplier?.data?.name}
            </Title>
            <Text
              type="secondary"
              style={{ fontWeight: "500", color: "grey", fontSize: "1rem" }}
            >
              {supplier?.data?.businessDisplayName}
            </Text>
          </div>

          <Divider />

          <div>
            <Title level={5} style={{ color: "#333" }}>
              About Me
            </Title>
            <p
              style={{
                color: "grey",
                fontWeight: "500",
                lineHeight: 1.6,
                fontSize: "1rem",
              }}
            >
              <Markdown>{supplier?.data?.profileData?.about_me}</Markdown>
            </p>
          </div>
          <div>
            <Title level={5} style={{ color: "#333" }}>
              What I Offer
            </Title>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                flexWrap: "wrap",
                fontWeight: "500",
              }}
            >
              {supplier?.data?.services_offered &&
                supplier?.data?.services_offered[0]?.map((service, index) => (
                  <GuideServicePill key={index} category={service} />
                ))}
            </div>
          </div>
          {supplier?.data?.spokenLanguage &&
            supplier?.data?.spokenLanguage[0]?.length > 0 && (
              <div>
                <Title level={5} style={{ color: "#333" }}>
                  Spoken Languages
                </Title>
                <div
                  style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                >
                  {supplier?.data?.spokenLanguage?.[0]?.map(
                    (language, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "0.3rem 0.6rem",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "15px",
                          fontSize: "0.9rem",
                          // color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {language}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
        </Modal>

        <ParticipantsControlModal
          guestModalVisible={guestModalVisible}
          setGuestModalVisible={setGuestModalVisible}
          handleGuestModalOk={() => {
            setGuestModalVisible(false);
          }}
          handleGuestChange={handleGuestChange}
          guests={guests}
          isShared={partyType === "Shared"}
          handleTabChange={handleTabChange}
          maxParticipants={tourData?.maxParticipants || 10}
          experienceType={tourData?.experienceType || "Shared and Private"}
        />
      </div>
    );
  } else {
    return (
      <div
        style={{
          backgroundColor: "#f0f2f5",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Navbar
          headerCheckoutButtonText={
            displayedDateTime ? "Checkout" : "Choose Date & Time"
          }
          headerCheckoutButtonTextOnClick={handleCheckout}
          breadcrumbs={breadcrumbs}
          headerDownloadButtonOnClick={() => setDownloadAppModalVisible(true)}
          switchOverWidth={displayedDateTime ? 1070 : 1150}
        />
        <div
          style={{
            height: "4.3rem",
          }}
        ></div>
        <LoadingSkeletonTourDetails />
      </div>
    );
  }
};
export default TourDetails;
